import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { SocialIcon, type SocialPlatform } from '../icons/SocialIcon';

interface TopPostItem {
  id: string;
  platform?: SocialPlatform | string;
  created_at?: string;
  content?: {
    text?: string;
    media_url?: string;
    thumbnail_url?: string;
    permalink?: string;
  };
  metrics?: Record<string, number>;
}

interface TopPostsCardProps {
  posts: TopPostItem[];
  onCreate?: () => void;
}

const getMediaEmojiFromUrl = (url?: string): string => {
  if (!url) return '📝';
  const lower = url.toLowerCase();
  if (/(\.mp4|\.mov|\.webm|\.avi|\.mkv|\.m4v)(\?|$)/.test(lower)) return '🎬';
  return '🖼️';
};
const getMediaLabelFromUrl = (url?: string): string => {
  if (!url) return 'Text';
  const lower = url.toLowerCase();
  if (/(\.mp4|\.mov|\.webm|\.avi|\.mkv|\.m4v)(\?|$)/.test(lower)) return 'Video';
  return 'Image';
};

const getMediaEmojiAndLabelFromPost = (post: any): { emoji: string; label: string } => {
  const rawType: string | undefined = post?.type || post?.content?.type;
  const normalized = (rawType || '').toString().toLowerCase();
  if (normalized === 'video') return { emoji: '🎬', label: 'Video' };
  if (normalized === 'image' || normalized === 'photo' || normalized === 'picture') return { emoji: '🖼️', label: 'Image' };
  if (normalized === 'text' || normalized === 'status') return { emoji: '📝', label: 'Text' };
  const url = post?.content?.media_url || post?.content?.thumbnail_url || post?.content?.permalink;
  return { emoji: getMediaEmojiFromUrl(url), label: getMediaLabelFromUrl(url) };
};

export const TopPostsCard: React.FC<TopPostsCardProps> = ({ posts, onCreate }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Debug: vérifier la langue et la traduction
  console.log('TopPostsCard - Current language:', i18n.language);
  console.log('TopPostsCard - Title translation:', t('analytics.topPosts.title'));

  if (!Array.isArray(posts) || posts.length === 0) {
    return (
      <section className="w-full bg-white rounded-xl border border-black shadow-lg p-3 sm:p-4">
              <div className="flex items-center gap-2 mb-2">
        <h3 className="text-lg font-semibold text-gray-900">{t('analytics.topPosts.title', '🏆 Your top posts from the last 30 days')}</h3>
      </div>
      <p className="text-sm text-gray-500">{t('analytics.topPosts.subtitle', 'Your most engaging content for the selected period.')}</p>
      <div className="min-h-[160px] flex flex-col items-center justify-center text-center">
          <div className="text-sm text-gray-600 mb-4">{t('analytics.topPosts.empty', 'No posts have been published last month.')}</div>
          <button
            onClick={() => (onCreate ? onCreate() : navigate('/content-creation'))}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t('sidebar.createContent', 'Create Content')}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full bg-white rounded-xl border border-black shadow-lg p-3 sm:p-4">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-lg font-semibold text-gray-900">{t('analytics.topPosts.title', '🏆 Your top posts from the last 30 days')}</h3>
      </div>
      <div className="space-y-3">
        {posts.slice(0, 5).map((post: any, idx: number) => {
          const thumb = post?.content?.thumbnail_url || post?.content?.media_url || '';
          const text: string = post?.content?.text || '';
          const snippet = text; // rely on CSS line-clamp-2 instead of manual slicing
          const createdAt = post?.created_at ? new Date(post.created_at) : null;
          const dateStr = createdAt && !isNaN(createdAt.getTime()) ? createdAt.toLocaleDateString() : '';
          const permalink = post?.content?.permalink;
          const metrics = post?.metrics || {};
          const postPlatform = post?.platform as SocialPlatform | undefined;
          const { emoji: mediaEmoji, label: mediaLabel } = getMediaEmojiAndLabelFromPost(post);
          const medal = idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : '';
          return (
            <div key={post.id} className="flex gap-3 p-3 border border-black rounded-lg bg-white">
              <div className="relative w-24 h-24 flex-shrink-0">
                <div className="w-full h-full border border-black overflow-hidden rounded-md bg-gray-50">
                  {thumb ? (
                    <img src={thumb} alt="post" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">No image</div>
                  )}
                </div>
                {medal && (
                  <div className="absolute -top-2 -right-2 w-9 h-9 bg-white border border-black rounded-md flex items-center justify-center shadow-sm z-10">
                    <span className="text-2xl leading-none" aria-hidden="true">{medal}</span>
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <p className="flex-1 min-w-0 text-sm font-medium text-gray-900 line-clamp-2 overflow-hidden">
                    {snippet || t('analytics.topPosts.noText', 'No text')}
                  </p>
                  {permalink && (
                    <a href={permalink} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 hover:underline flex-shrink-0 ml-2">
                      {t('analytics.topPosts.view', 'Open')}
                    </a>
                  )}
                </div>
                <div className="mt-1 text-xs text-gray-500 flex items-center gap-2">
                  <span>{dateStr}</span>
                  {postPlatform && (
                    <span className="group relative inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full bg-white border border-gray-300 text-gray-700">
                      <SocialIcon platform={postPlatform} size={12} />
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full bg-primary-50 border border-primary-200 text-primary-700">
                    {mediaEmoji} {mediaLabel}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {'likes' in metrics && (
                    <span className="group relative inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-black bg-white">
                      <span className="text-[13px]">❤️</span> <strong className="font-semibold" style={{ fontFamily: 'Bricolage Grotesque, ui-sans-serif, system-ui' }}>{metrics.likes}</strong>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-primary-100 text-gray-900 text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                        {t('analytics.metrics.likes','Likes')}
                      </div>
                    </span>
                  )}
                  {'comments' in metrics && (
                    <span className="group relative inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-black bg-white">
                      <span className="text-[13px]">💬</span> <strong className="font-semibold" style={{ fontFamily: 'Bricolage Grotesque, ui-sans-serif, system-ui' }}>{metrics.comments}</strong>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-primary-100 text-gray-900 text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                        {t('analytics.metrics.comments','Comments')}
                      </div>
                    </span>
                  )}
                  {'shares' in metrics && (
                    <span className="group relative inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-black bg-white">
                      <span className="text-[13px]">🔗</span> <strong className="font-semibold" style={{ fontFamily: 'Bricolage Grotesque, ui-sans-serif, system-ui' }}>{metrics.shares}</strong>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-primary-100 text-gray-900 text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                        {t('analytics.metrics.shares','Shares')}
                      </div>
                    </span>
                  )}
                  {'clicks' in metrics && (
                    <span className="group relative inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-black bg-white">
                      <span className="text-[13px]">👉</span> <strong className="font-semibold" style={{ fontFamily: 'Bricolage Grotesque, ui-sans-serif, system-ui' }}>{metrics.clicks}</strong>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-primary-100 text-gray-900 text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                        {t(postPlatform === 'facebook' ? 'analytics.metrics.pageClicks' : 'analytics.metrics.clicks', postPlatform === 'facebook' ? 'Page Clicks' : 'Clicks')}
                      </div>
                    </span>
                  )}
                  {'views' in metrics && (
                    <span className="group relative inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-black bg-white">
                      <span className="text-[13px]">👁️</span> <strong className="font-semibold" style={{ fontFamily: 'Bricolage Grotesque, ui-sans-serif, system-ui' }}>{metrics.views}</strong>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-primary-100 text-gray-900 text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                        {t('analytics.metrics.views','Views')}
                      </div>
                    </span>
                  )}
                  {'unique_views' in metrics && (
                    <span className="group relative inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-black bg-white">
                      <span className="text-[13px]">🔍</span> <strong className="font-semibold" style={{ fontFamily: 'Bricolage Grotesque, ui-sans-serif, system-ui' }}>{metrics.unique_views}</strong>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-primary-100 text-gray-900 text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                        {t('analytics.metrics.uniqueViews','Unique Views')}
                      </div>
                    </span>
                  )}
                  {'engagement_rate' in metrics && (
                    <span className="group relative inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-black bg-white">
                      <span className="text-[13px]">🔥</span> <strong className="font-semibold" style={{ fontFamily: 'Bricolage Grotesque, ui-sans-serif, system-ui' }}>{`${Math.round(typeof metrics.engagement_rate === 'number' ? metrics.engagement_rate : Number(metrics.engagement_rate || 0))}%`}</strong>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                        {t('analytics.metrics.engagementRate','Engagement Rate')}
                      </div>
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default TopPostsCard;


